/**
 * XML ↔ JSON with the conventions spelled out as options.
 *
 * XML has attributes, text beside child elements, repeated elements and element order;
 * JSON has none of those, so every converter invents a convention. This one lets you
 * choose it: the prefix on attribute names, the key that holds text, whether repeated
 * elements become arrays (or every child does), whether numbers and booleans are typed,
 * how empty elements read, and whether the root element's name is kept.
 */

import {
  DataError,
  type DataMap,
  type DataValue,
  type DataWarning,
  isMap,
  type JsonIndent,
  RawNumber,
  stringifyJson,
} from "@/lib/tool-kit/latex-dev/data-model";
import { parseJson } from "@/lib/tool-kit/latex-dev/json-parse";
import { parseXml, type XmlElement } from "@/lib/tool-kit/latex-dev/xml-parse";

export type AttributePrefix = "@" | "@_" | "_" | "-";
export type TextKey = "#text" | "_text" | "$" | "text";
export type XmlIndent = "2" | "4" | "tab" | "compact";

export interface XmlJsonOptions {
  attrPrefix: AttributePrefix;
  textKey: TextKey;
  /** `repeated`: only elements that occur more than once become arrays. */
  arrays: "repeated" | "always";
  /** Trim text and drop white space between elements. */
  trim: boolean;
  /** Read numbers and true/false in text and attributes as JSON numbers and booleans. */
  typed: boolean;
  empty: "null" | "string";
  /** Keep the root element's name as the outer key. */
  includeRoot: boolean;
  jsonIndent: JsonIndent;
  xmlIndent: XmlIndent;
  declaration: boolean;
  /** Name for the root element when JSON → XML has to invent one. */
  rootName: string;
}

export const DEFAULT_XML_JSON_OPTIONS: XmlJsonOptions = {
  attrPrefix: "@",
  textKey: "#text",
  arrays: "repeated",
  trim: true,
  typed: false,
  empty: "null",
  includeRoot: true,
  jsonIndent: "2",
  xmlIndent: "2",
  declaration: true,
  rootName: "root",
};

export interface XmlSummary {
  root: string;
  elements: number;
  attributes: number;
  depth: number;
}

export interface XmlProblem {
  code: string;
  params: Readonly<Record<string, string | number>>;
  line: number;
  column: number;
  excerpt: string;
}

export type XmlOutcome =
  | { ok: true; text: string; summary: XmlSummary; warnings: DataWarning[] }
  | { ok: false; problem: XmlProblem };

const OUTPUT_LIMIT = 5_000_000;
const EXCERPT_LIMIT = 120;
const RE_LINE_BREAK = /\r\n?|\n/;
const RE_JSON_NUMBER = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?$/;
const RE_NAME_FULL = /^[\p{L}_:][\p{L}\p{N}_:.\-·]*$/u;
const RE_TEXT_ESCAPE = /[&<>]/g;
const RE_ATTR_ESCAPE = /[&<"\t\n\r]/g;

// ---------------------------------------------------------------------------
// XML → JSON
// ---------------------------------------------------------------------------

interface ToJsonContext {
  options: XmlJsonOptions;
  warnings: DataWarning[];
}

function typedScalar(text: string, options: XmlJsonOptions): DataValue {
  if (!options.typed) {
    return text;
  }
  if (text === "true" || text === "false") {
    return text === "true";
  }
  if (RE_JSON_NUMBER.test(text)) {
    const n = Number(text);
    return String(n) === text ? n : new RawNumber(text);
  }
  return text;
}

function textOf(element: XmlElement, options: XmlJsonOptions): string {
  const pieces = element.children.filter(
    (c): c is string => typeof c === "string"
  );
  if (options.trim) {
    return pieces
      .map((p) => p.trim())
      .filter((p) => p !== "")
      .join(" ");
  }
  const joined = pieces.join("");
  const hasElements = element.children.some((c) => typeof c !== "string");
  return hasElements && joined.trim() === "" ? "" : joined;
}

function addChild(
  obj: DataMap,
  arrayKeys: Set<string>,
  reserved: Set<string>,
  name: string,
  value: DataValue,
  ctx: ToJsonContext,
  line: number
): void {
  if (reserved.has(name)) {
    ctx.warnings.push({ code: "keyCollision", line, params: { key: name } });
  }
  const existing = obj.get(name);
  if (existing === undefined && !obj.has(name)) {
    const always = ctx.options.arrays === "always";
    obj.set(name, always ? [value] : value);
    if (always) {
      arrayKeys.add(name);
    }
  } else if (arrayKeys.has(name)) {
    (existing as DataValue[]).push(value);
  } else {
    obj.set(name, [existing as DataValue, value]);
    arrayKeys.add(name);
  }
}

function elementValue(element: XmlElement, ctx: ToJsonContext): DataValue {
  const { options } = ctx;
  const text = textOf(element, options);
  const children = element.children.filter(
    (c): c is XmlElement => typeof c !== "string"
  );
  if (element.attrs.length === 0 && children.length === 0) {
    if (text === "") {
      return options.empty === "null" ? null : "";
    }
    return typedScalar(text, options);
  }
  if (text !== "" && children.length > 0) {
    ctx.warnings.push({
      code: "mixedContent",
      line: element.line,
      params: { name: element.name },
    });
  }
  const obj: DataMap = new Map();
  const reserved = new Set<string>();
  for (const [name, value] of element.attrs) {
    const key = `${options.attrPrefix}${name}`;
    obj.set(key, typedScalar(value, options));
    reserved.add(key);
  }
  if (text !== "") {
    obj.set(options.textKey, typedScalar(text, options));
    reserved.add(options.textKey);
  }
  const arrayKeys = new Set<string>();
  for (const child of children) {
    addChild(
      obj,
      arrayKeys,
      reserved,
      child.name,
      elementValue(child, ctx),
      ctx,
      child.line
    );
  }
  return obj;
}

function measure(element: XmlElement): {
  elements: number;
  attributes: number;
  depth: number;
} {
  let elements = 1;
  let attributes = element.attrs.length;
  let deepest = 0;
  for (const child of element.children) {
    if (typeof child !== "string") {
      const inner = measure(child);
      elements += inner.elements;
      attributes += inner.attributes;
      deepest = Math.max(deepest, inner.depth);
    }
  }
  return { elements, attributes, depth: deepest + 1 };
}

function excerptOf(source: string, line: number): string {
  const text = source.split(RE_LINE_BREAK)[line - 1] ?? "";
  return text.length > EXCERPT_LIMIT
    ? `${text.slice(0, EXCERPT_LIMIT)}…`
    : text;
}

function problemFrom(error: unknown, source: string): XmlProblem {
  if (error instanceof DataError) {
    return {
      code: error.code,
      params: error.params,
      line: error.line,
      column: error.column,
      excerpt: error.line > 0 ? excerptOf(source, error.line) : "",
    };
  }
  const code = error instanceof RangeError ? "tooDeep" : "internal";
  return { code, params: {}, line: 0, column: 0, excerpt: "" };
}

/** Convert XML text to JSON text. */
export function xmlToJson(source: string, options: XmlJsonOptions): XmlOutcome {
  try {
    const { root, warnings } = parseXml(source);
    const ctx: ToJsonContext = { options, warnings: [...warnings] };
    const value = elementValue(root, ctx);
    const output: DataValue = options.includeRoot
      ? new Map([[root.name, value]])
      : value;
    const stats = measure(root);
    return {
      ok: true,
      text: stringifyJson(output, options.jsonIndent, OUTPUT_LIMIT),
      summary: { root: root.name, ...stats },
      warnings: ctx.warnings,
    };
  } catch (error) {
    return { ok: false, problem: problemFrom(error, source) };
  }
}

// ---------------------------------------------------------------------------
// JSON → XML
// ---------------------------------------------------------------------------

const ITEM_NAME = "item";

interface Writer {
  options: XmlJsonOptions;
  unit: string;
  lines: string[];
  elements: number;
  attributes: number;
  depth: number;
}

function fail(code: string, params: Record<string, string | number>): never {
  throw new DataError(code, 0, 0, params);
}

const TEXT_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
};

function escapeText(text: string): string {
  return text.replace(RE_TEXT_ESCAPE, (c) => TEXT_ESCAPES[c]);
}

const ATTR_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  '"': "&quot;",
  "\t": "&#9;",
  "\n": "&#10;",
  "\r": "&#13;",
};

function escapeAttribute(text: string): string {
  return text.replace(RE_ATTR_ESCAPE, (c) => ATTR_ESCAPES[c]);
}

function isScalar(value: DataValue): boolean {
  return !(Array.isArray(value) || isMap(value));
}

function isXmlCode(code: number): boolean {
  return (
    code === 9 ||
    code === 10 ||
    code === 13 ||
    (code >= 32 && code <= 0xd7_ff) ||
    (code >= 0xe0_00 && code <= 0xff_fd) ||
    (code >= 0x1_00_00 && code <= 0x10_ff_ff)
  );
}

function scalarText(value: DataValue, path: string): string {
  let text: string;
  if (value === null) {
    text = "";
  } else if (value instanceof RawNumber) {
    text = value.text;
  } else {
    text = String(value);
  }
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (!isXmlCode(code)) {
      fail("xmlBadChar", {
        path,
        code: `U+${code.toString(16).toUpperCase().padStart(4, "0")}`,
      });
    }
  }
  return text;
}

function checkName(name: string, path: string): void {
  if (!RE_NAME_FULL.test(name)) {
    fail("xmlInvalidName", { name, path });
  }
}

function indentFor(w: Writer, depth: number): string {
  return w.unit === "" ? "" : w.unit.repeat(depth);
}

function push(w: Writer, depth: number, text: string): void {
  w.lines.push(`${indentFor(w, depth)}${text}`);
}

/** Split a mapping into attributes, the text, and child elements. */
function splitMap(
  map: DataMap,
  w: Writer,
  path: string
): { attrs: string; text: string | null; children: [string, DataValue][] } {
  const { attrPrefix, textKey } = w.options;
  let attrs = "";
  let text: string | null = null;
  const children: [string, DataValue][] = [];
  for (const [key, value] of map) {
    if (key === textKey) {
      if (!isScalar(value)) {
        fail("xmlTextNotScalar", { path: `${path}/${key}` });
      }
      text = scalarText(value, `${path}/${key}`);
    } else if (key.startsWith(attrPrefix) && key.length > attrPrefix.length) {
      const name = key.slice(attrPrefix.length);
      checkName(name, `${path}/${key}`);
      if (!isScalar(value)) {
        fail("xmlAttrNotScalar", { key, path });
      }
      attrs += ` ${name}="${escapeAttribute(scalarText(value, `${path}/${key}`))}"`;
      w.attributes++;
    } else {
      children.push([key, value]);
    }
  }
  return { attrs, text, children };
}

function writeElement(
  w: Writer,
  name: string,
  value: DataValue,
  depth: number,
  path: string
): void {
  checkName(name, path);
  if (Array.isArray(value)) {
    writeRepeated(w, name, value, depth, path);
    return;
  }
  w.elements++;
  w.depth = Math.max(w.depth, depth + 1);
  if (value === null) {
    push(w, depth, `<${name}/>`);
    return;
  }
  if (!isMap(value)) {
    push(w, depth, `<${name}>${escapeText(scalarText(value, path))}</${name}>`);
    return;
  }
  const { attrs, text, children } = splitMap(value, w, path);
  if (children.length === 0) {
    const body = text === null ? "" : escapeText(text);
    push(
      w,
      depth,
      text === null || text === ""
        ? `<${name}${attrs}/>`
        : `<${name}${attrs}>${body}</${name}>`
    );
    return;
  }
  push(w, depth, `<${name}${attrs}>${text === null ? "" : escapeText(text)}`);
  for (const [childName, childValue] of children) {
    writeElement(w, childName, childValue, depth + 1, `${path}/${childName}`);
  }
  push(w, depth, `</${name}>`);
}

function writeRepeated(
  w: Writer,
  name: string,
  list: DataValue[],
  depth: number,
  path: string
): void {
  list.forEach((item, index) => {
    if (Array.isArray(item)) {
      fail("xmlNestedArray", { path: `${path}[${index}]` });
    }
    writeElement(w, name, item, depth, `${path}[${index}]`);
  });
}

function rootChoice(
  value: DataValue,
  options: XmlJsonOptions
): { name: string; content: DataValue } {
  if (options.includeRoot && isMap(value) && value.size === 1) {
    const [[key, content]] = [...value];
    const isAttribute =
      key.startsWith(options.attrPrefix) || key === options.textKey;
    if (!(isAttribute || Array.isArray(content))) {
      return { name: key, content };
    }
  }
  return { name: options.rootName, content: value };
}

const XML_INDENTS: Readonly<Record<XmlIndent, string>> = {
  "2": "  ",
  "4": "    ",
  tab: "\t",
  compact: "",
};

/** Convert JSON text to XML text. */
export function jsonToXml(source: string, options: XmlJsonOptions): XmlOutcome {
  try {
    const { value, warnings } = parseJson(source);
    const w: Writer = {
      options,
      unit: XML_INDENTS[options.xmlIndent],
      lines: [],
      elements: 0,
      attributes: 0,
      depth: 0,
    };
    const { name, content } = rootChoice(value, options);
    // A bare list under the root becomes repeated <item> elements.
    if (Array.isArray(content)) {
      checkName(name, name);
      w.elements++;
      w.depth = 1;
      push(w, 0, `<${name}>`);
      writeRepeated(w, ITEM_NAME, content, 1, name);
      push(w, 0, `</${name}>`);
    } else {
      writeElement(w, name, content, 0, name);
    }
    const body = w.lines.join(w.unit === "" ? "" : "\n");
    const head = options.declaration
      ? `<?xml version="1.0" encoding="UTF-8"?>${w.unit === "" ? "" : "\n"}`
      : "";
    const text = `${head}${body}`;
    if (text.length > OUTPUT_LIMIT) {
      fail("tooLarge", { limit: OUTPUT_LIMIT });
    }
    return {
      ok: true,
      text,
      summary: {
        root: name,
        elements: w.elements,
        attributes: w.attributes,
        depth: w.depth,
      },
      warnings,
    };
  } catch (error) {
    return { ok: false, problem: problemFrom(error, source) };
  }
}
